// This file contains interfaces for common models used in several places
import type { TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import type { SearchContentType } from '@/services/Search';
import type {
    Asset,
    CapGenItem,
    HierarchicalDataPoint,
    MapGeometry,
    Tool,
    TranslationDetails,
    UnitValue,
    WebLink,
    WindTurbineSummary,
} from 'bnef';
import type { NextWebVitalsMetric } from 'next/app';

export interface ISimpleResultItem<T> {
    id: T;
    name: string;
}

export interface ILink {
    label: string;
    url: string;
}

/** @see api/web/src/Security/SessionUser.php */
export interface ISessionUser {
    id: number;
    isGuestUser: boolean;
    isInternalUser: boolean;
    l10n: string;
    bbUuid: number;
    type: string;
    trialExpiry?: string;
    hasSetInterests?: boolean;
    regulationRestrictions: string[];
    enabledFeatures?: string[];
}

// todo - replace with type
export interface IPagination {
    current: number;
    pageSize: number;
    total?: number;
}

/** @see Domain/Entity/Person.php */
export interface IPerson {
    id: string;
    displayName: string;
    name: string; // without any titles
    url: string;
    imageUrl?: string;
    position?: string;
    address?: string[];
    phoneNumber?: string;
    email?: string;
    organization?: string;
}

/** @see Service/EventsService.php */
export interface IRole {
    role: string;
    people: IPerson[];
}

export interface IChart {
    id?: string;
    title?: string;
    description?: string;
    source?: string;
    note?: string;
    toasterData?: IToasterData;
}

export interface IToasterData {
    url: string; // Toaster iframe URL
    id: string; // Toaster UUID
    aspectRatio: string;
    responsiveImages: {
        dark?: { url: string };
        light?: { url: string };
    };
}

/**
 * @desc Short Detail
 * @see api/src/Controller/Shorts/ShowController.php
 */
export interface IShortDetail extends IContentItem {
    body: string;
    tags: IContentTags;
    byPeople?: IPerson[];
    parentInsightNote: IContentItem | null;
    parentCountryProfile: IContentItem | null;
    parentInteractiveDataset: any;
    parentNewsStory: IContentItem | null;
    parentTheme: IContentItem | null;
    originalLanguage: string;
    translationDetails?: TranslationDetails;
    suid?: string;
}

/**
 * @desc Analyst Reaction Detail
 * @see api/src/Controller/AnalystReactions/ShowController.php
 */
export interface IAnalystReactionDetail extends IContentItem {
    body: string;
    tags: IContentTags;
    byPeople?: IPerson[];
    originalLanguage: string;
    translationDetails?: TranslationDetails;
    suid?: string;
}

/** All the content types that can be supported in generic items */
export enum ContentType {
    countryprofile = 'countryprofile',
    event = 'event',
    flagship = 'flagship',
    forum = 'forum',
    insight = 'insight',
    interactivedataset = 'interactivedataset',
    news = 'news',
    policy = 'policy',
    publication = 'publication',
    publicwebinar = 'publicwebinar',
    series = 'series',
    sector = 'sector',
    short = 'short',
    analystreaction = 'analystreaction',
    summit = 'summit',
    terminal = 'terminal',
    theme = 'theme',
    video = 'video',
    webinar = 'webinar',
    data = 'data',
    neoinsights_22 = 'neoinsights_22',
    model = 'model',
    excelmodel = 'excelmodel', // todo - this can become a subtype with refactoring
    insightdataset = 'insightdataset', // todo - this can become a subtype with refactoring
    dataset = 'dataset', // todo - this can become a subtype with refactoring
    tool = 'tool',
    geoeconomics = 'geoeconomics',
    eventsession = 'eventsession',
}

export enum PublicWebinarSubtype {
    webinar = 'webinar',
    roundtable = 'roundtable',
    conference = 'conference',
    regional = 'regional',
}

export enum DatasetType {
    organization = 'organization',
    financing = 'financing',
    policy = 'policy',
    windTurbine = 'windTurbine',
    power = 'asset_power',
    storage = 'asset_storage',
    person = 'person',
    batteryPlant = 'batteryPlant',
    hydrogenAsset = 'hydrogenAsset',
    solarManufacturingAsset = 'solarManufacturingAsset',
    energyPPA = 'energyPPA',
}

/**
 * @see `Webapp\Domain\ValueObject\ContentStatus`
 */
export type IContentStatus = 'new' | 'updated' | 'archived';

export interface IPinableItem {
    url: string;
    pinned?: boolean;
}

/**
 * @see `Webapp\Domain\ValueObject\Image\Image`
 */
export interface IImage {
    baseUrl: string;
    darkUrl?: string;
}

/**
 * @description A generic interface covering any type of content item
 * @see `Webapp\Domain\Entity\Traits\ListItemTrait`
 */
export interface IContentItem {
    id: string;
    type: ContentType;
    url: string;
    title: string;
    leadImage?: IImage;
    displayDate: string;
    summary?: string;
    contentStatus?: IContentStatus;
    flags?: string[];
    tags?: IContentTags;
    videoDuration?: number;
    videoResourceId?: string;
    videoPolyvId?: string;
    event?: IEvent;
    eventSubtype?: PublicWebinarSubtype;
    parentSeries?: ISeriesItem | null;
    insightType?: {
        id: number;
        name: string;
        deprecated: boolean;
        urlKey: string;
    };
}

export interface IAttachment {
    type: 'pdf' | 'pdfFull' | 'abstract' | 'powerpoint' | 'excel' | 'zip';
    url: string;
}

export interface IInsightNoteDetail extends IContentItem {
    abstract: string;
    isPublished: boolean;
    attachments?: IAttachment[];
    tags?: IContentTags;
    leadAuthor: IPerson;
    bulletPoints: string[];
    chart: IChart | null;
    embeddedVideo: IEmbeddedVideo | null;
    mrcVideoId: string | null;
    byTheNumbers: {
        heading: string;
        value: string;
    }[];
    translationDetails?: TranslationDetails;
}

export interface IEmbeddedVideo {
    provider: 'Vimeo';
    id: string;
    title: string | null;
    description: string | null;
    source: string | null;
}

export interface IModelDetail {
    changeLog?: string;
}

/** These properties can be used to identify a component and its descendants within their placement context. */
export interface IAnalyticsTraceableElement {
    analyticsBaseId?: string;
    analyticsCategory?: TrackingCategory;
    position?: number;
}

export type IContentTags = string[];

export interface ISeriesItem {
    id: string;
    isArchived: boolean;
    title: string;
    leadImage: IImage;
    url: string;
    latestInsight?: IContentItem;
    description: string;
    updateFrequency: string;
    isSubscribed?: boolean;
    contentStatus?: IContentStatus;
    tags?: IContentTags;
    type?: string;
}

export interface ISubscriptionState {
    nextAction: {
        url: string;
        action: 'subscribe' | 'unsubscribe';
    };
    isSubscribed: boolean;
}

export interface IContentSearchResponse<T = IContentItem> {
    pagination: IPagination;
    items: T[];
}

export interface IChartSearchResult extends IContentItem {
    chart: IChart;
}

export interface IPolicyTimelineEntry {
    date: string;
    description: string;
}

export interface IPolicySummary extends IResultItem {
    status: HierarchicalDataPoint;
    contentStatus?: IContentStatus;
    markets?: {
        id?: string; // ISO3
        name: string;
    }[];
    jurisdiction?: string;
    startDate?: string;
    expiryDate?: string;
    summary?: string;
    announcementDate?: string;
    targetDeadlineDate?: string;
    types?: HierarchicalDataPoint[];
    focusAreas?: HierarchicalDataPoint[];
    applications?: HierarchicalDataPoint[];
    targetGroups?: HierarchicalDataPoint[];
    latestTimelineEntry?: IPolicyTimelineEntry;
}

export interface IPolicy extends IPolicySummary {
    description: string | null;
    sources: WebLink[];
    timeline?: null | IPolicyTimelineEntry[];
}

export interface IPolicyMetric {
    id: string;
    name: string;
    unit: string | null;
    currency: string | null;
}

export type MetricValue = {
    value: number;
    note: null | string;
    policy: IPolicySummary;
};

export interface IResultItem {
    id: string;
    type: ContentType | DatasetType;
    title: string;
    url: string;
    summary?: string;
}

export interface IHardwareTurbine {
    numberOf: number;
    windTurbine: WindTurbineSummary;
}

export interface IPowerPlantGenerator {
    id: number;
    name: string;
    netCapacity: number;
    startYear: number | null;
    retirementYear: number | null;
    projectStatus?: HierarchicalDataPoint;
    primaryTechnologySubtype: string;
    technology?: HierarchicalDataPoint[];
    generatorInputs?: HierarchicalDataPoint[];
}

export type Platform = 'web' | 'terminal';

export interface IHelpCenterResourceItem {
    id: string;
    group?: string | null;
    title: string;
    description: string;
    videoId?: string | null;
    coverImage?: IImage | null;
    approximateVideoLengthMinutes?: number | null;
    platforms: Platform[];
}

export interface IHelpCenterResourceItemDetail extends IHelpCenterResourceItem {
    transcript?: string;
    relatedUrl?: string;
    relatedUrlLabel?: string;
    headings?: { id: string; title: string }[];
}

/** @see Domain/ValueObject/UserActivityItem.php */
export interface IUserActivityItem {
    dateTime: string;
    item: IContentItem;
}

/** @see Domain/Entity/Event/AbstractEvent.php */
export interface IEvent {
    id: string;
    title: string;
    location?: string;
    type: ContentType;
    subtype?: PublicWebinarSubtype;
    url: string;
    startDate: string;
    endDate: string;
    isDateProvisional: boolean;
    localTimeZone?: string;
    marketingUrl?: string;
    description?: string;
    registrationUrl?: string;
    joinUrl?: string; // todo - #SEMR - temporary until SEMR supports registrations
    leadImage?: IImage;
}

/** @see Domain/Entity/Event/Webinar.php */
export interface IEventWebinar extends IEvent {
    isAvailableForRegistration: boolean;
    subscriptionState: ISubscriptionState;
}

export interface IEventPublication extends IEvent {
    series?: ISeriesItem;
}

export interface ISummitEvent extends IEvent {
    embedUrl?: string;
}

/** @see Domain/Entity/Event/AbstractEvent.php */
export interface IEventSession extends IContentItem {
    description?: string;
    videoResourceType?: 'bloomberg' | 'vimeo';
    subjectInsight?: IContentItem;
    hasSlides?: boolean;
    hasData?: boolean;
    slideViewable?: boolean;
    event: IEvent;
}

export interface ISignupOptions {
    countryList: IFormChoice[];
    countrySubDivisionList: { [key: string]: IFormChoice[] };
    localesList: IFormChoice[];
    rolesList: IFormChoice[];
}

export interface IFormChoice<V = string> {
    label: string;
    value: V;
    selected?: boolean;
    iconName?: string;
    tooltip?: string;
}

export type IContentSearch = {
    contentType: SearchContentType;
    q?: string;
    rankingMethod?: 'relevance' | 'date';
    dateFrom?: string;
    dateTo?: string;
    country?: string[];
    'country-group'?: string[];
    sector?: string[];
    author?: string[];
    language?: string[];
    category?: string[];
    page?: string;
    pageSize?: string;
};

export type IResearchSearch = {
    q?: string;
    type?: SearchContentType;
    sort?: 'relevance' | 'date';
    page?: number;
    pageSize?: number;
    sector?: string[];
    region?: string[];
};

export interface ISectorPage {
    id: string;
    title: string;
    leadImage?: IImage;
    secondaryImage?: IImage;
    isArchived: boolean;
    iconSvg?: string;
    iconAnimation?: object;
    url: string;
    type?: string;
}

export interface IRelatedAssets {
    type: string;
    items: Asset[];
    url?: string;
}

export type IRelatedContent = IContentItem | ISeriesItem | ISectorPage | Tool;

export interface IToolPage {
    tool: Tool;
    relatedContent: IRelatedContent[];
}

export type RoleOption = { urlKey: string; label: string };

export enum SectorPageSections {
    research = 'research',
    dataAndTools = 'dataAndTools',
    news = 'news',
    shorts = 'shorts',
    // organizations = 'organizations',
    assets = 'assets',
    // policies = 'policies',
    themes = 'themes',
    series = 'series',
    videos = 'videos',
}

export interface ICardinalData {
    latitude: number;
    longitude: number;
}

export type CapGenBreakdownValue = 'energy-technology' | 'market' | 'continent';
export type CapGenScenarioValue = 'high' | 'mid' | 'low';

export type MapsBreakdownValue =
    | 'components'
    | 'plant-type'
    | 'status'
    | 'chemistry'
    | 'production-method'
    | 'usage-category'
    | 'technology'
    | 'input'
    | 'application';

export enum BatteryPlantType {
    cells = 'CELLS',
    components = 'COMPONENTS',
    packs = 'PACK',
    recycling = 'RECYCLING',
}

export enum HydrogenAssetType {
    production = 'Production',
    usage = 'Usage',
    storage = 'Storage',
    transport = 'Transport',
}

export enum AssetType {
    power = 'power',
    storage = 'storage',
}

export interface ICapGenList {
    solar?: CapGenItem;
    wind?: CapGenItem;
    geothermal?: CapGenItem;
    biomass?: CapGenItem;
    hydro?: CapGenItem;
    nuclear?: CapGenItem;
    gas?: CapGenItem;
    oil?: CapGenItem;
    coal?: CapGenItem;
    fossil?: CapGenItem;
    marine?: CapGenItem;
    onshore?: CapGenItem;
    offshore?: CapGenItem;
    thermal?: CapGenItem;
    'pv-utility'?: CapGenItem;
    'pv-residential'?: CapGenItem;
    'pv-commercial'?: CapGenItem;
    'pv-unknown'?: CapGenItem;
}

export interface ICapGenData extends ICapGenList {
    year: number;
    type: string;
    totalValue?: number | null;
}

interface ISource {
    geoData: IGeoJSON;
    legendData: ILegendData;
}

export interface IGeoData {
    [key: string]: ISource;
}

export interface ILegendData {
    color?: string;
    text?: string;
    isLine?: boolean;
    value?: number;
    dateValue?: string;
    nonNumeralValue?: string;
    reference?: string;
    translationKey?: string;
    name?: string;
    displayName?: string;
    additionalData?: string | number; // used for populating third column in chart tooltips
    additionalDataSuffix?: string;
}

type Properties = {
    id: string;
    value: number;
    // Mapbox stringifies nested feature properties, and geoJsonRenderer JSON.parses this back out.
    // Hand-built sources therefore pass a pre-serialised string; see mapUtils.tsx processCountryMapResponse.
    popupData?: PopupData | string;
};

export type Geometry = { type: 'Point'; coordinates: [number, number] } | MapGeometry;

export interface IFeature {
    type: string;
    geometry: Geometry;
    properties: Properties;
}

export interface IGeoJSON {
    type: string;
    features: IFeature[];
}

export type PopupDataColumn = {
    link?: string;
    text: string;
};

export type PopupData = {
    /** @deprecated */
    data?: PopupDataProperty[];
    accuracy?: number;
    location?: string;
    columns?: PopupDataColumn[][];
};

export type MapGroup = {
    id: string;
    displayName?: string;
};

export type PopupDataProperty = {
    mapGroup: MapGroup;
    url: string;
    title: string;
    location?: string;
    capacity?: UnitValue;
    output?: number;
    assetType?: DatasetType.power | DatasetType.storage;
    type?: string;
    plantPhase?: string;
};

export type PopupLabels = {
    headings: Record<string, string>;
    approximationLevel: Record<string, string>;
};

export type ExportColumn = {
    id: string;
    groupId: string;
    title: string;
    exportName: string;
    description?: string | null;
    dataType: 'number' | 'string' | 'date';
    defaultSelected?: boolean;
    required?: boolean;
    // Which sheet the column belongs to: the base dataset key, or a relationship tab id.
    tabId?: string;
    displayOrder?: number;
};

export type ExportColumnGroup = {
    groupId: string;
    label: string;
    tabId?: string;
    // 'tab' groups export to a separate Excel sheet; 'base'/'inline' live on the main sheet.
    kind?: 'base' | 'inline' | 'tab' | 'organization-by-role' | 'role';
    // Parent group id for nested relationship groups; null/absent for top-level groups.
    parentGroupId?: string | null;
    displayOrder?: number;
};

// The export schema for a dataset: its display name and the columns/groups available to export.
export type ExportSchema = {
    name: string;
    columns: ExportColumn[];
    columnGroups: ExportColumnGroup[];
};

// GET /datasets/{dataset} from the Python API: a dataset's name, description and
// resolved filter list, in the frontend's own bnef.Filter wire shape.
export type DatasetDetailsFilterOption = {
    value: string;
    displayName: string;
    // A region filter's group options carry their member countries this way.
    options: DatasetDetailsFilterOption[] | null;
    // A region country with states, or a hierarchy node with children, that is itself selectable as well as a parent to expand.
    selectSelfOnly: boolean;
};

export type DatasetDetailsDateField = {
    id: string;
    label: string;
    param: string;
};

export type DatasetDetailsDatePreset = {
    id: string;
    label: string;
};

export type DatasetDetailsDateComponentConfig = {
    fields: DatasetDetailsDateField[];
    presets: DatasetDetailsDatePreset[];
    minDate: string | null;
    maxDate: string | null;
};

// An `organization` filter resolved by remote search rather than a listed set of options.
export type DatasetDetailsSearchComponentConfig = {
    dataset: string;
};

export type DatasetDetailsFilter = {
    id: string;
    param: string;
    type: string;
    label: string;
    searchable: boolean;
    allowMultiple: boolean;
    options: DatasetDetailsFilterOption[];
    componentConfig: DatasetDetailsDateComponentConfig | DatasetDetailsSearchComponentConfig | null;
    // Another dataset's own filter, carried alongside a search filter — e.g. an asset's
    // organization search alongside organization's own BICS filter.
    secondaryFilter: DatasetDetailsFilter | null;
};

export type DatasetDetails = {
    key: string;
    name: string;
    description: string | null;
    textSearch: { param: string } | null;
    filters: DatasetDetailsFilter[];
};

// The /datasets payload: every dataset export plus the linked-records they reference, used by the
// exports landing page (cards + relationship diagram) and the cross-export title dropdown. Mirrors
// Webapp\Data\Dataset\DatasetSchemaRepository::listDatasets().
export type DatasetSummaryColumn = {
    title: string;
    exportName: string;
};

export type DatasetRelationship = {
    name: string;
    /** Triple-store predicate for the diagram edge (Subject — predicate → Object); falls back to name. */
    predicate: string;
    target: string;
    targetKind: 'dataset' | 'linkedRecord';
    type: string;
    mode: string | null;
};

export type DatasetSummary = {
    key: string;
    name: string;
    description: string | null;
    /** Standalone export page URL, or null for inline-only entities (e.g. organization). */
    exportUrl: string | null;
    columns: DatasetSummaryColumn[];
    relationships: DatasetRelationship[];
};

export type LinkedRecordSummary = {
    key: string;
    name: string;
    description: string | null;
    columns: DatasetSummaryColumn[];
    relationships?: DatasetRelationship[];
};

export type DatasetsGraph = {
    datasets: DatasetSummary[];
    linkedRecords: LinkedRecordSummary[];
};

// How a base record's multiple related records are folded into the export. Mirrors the backend
// Webapp\Data\Dataset\Export\MultipleValuesMode enum.
export type MultipleValuesMode = 'tabs' | 'duplicateRows' | 'mergeRows' | 'byColumn';

export type ExportColumnPreviewRecord = Record<string, string | number | null>;

export type ExportPreviewTab = {
    id: string;
    label: string;
    columns: ExportColumn[];
    records: ExportColumnPreviewRecord[];
};

export type ExportOptInsOption = {
    id: string;
    name: string;
};

export enum TourKeys {
    // Always lower case
    SearchPage = 'search-page',
    HomePage = 'home-page-promo',
}

export enum TourTargets {
    // Global
    allFilters = 'allFilters',
    singleFilter = 'singleFilter',
    navSearchIcon = 'navSearchIcon',

    // Search Page
    searchInput = 'searchInput',
    searchNav = 'searchNav',
    searchSubPages = 'searchSubPages',
    searchResults = 'searchResults',
    searchSort = 'searchSort',
    chartSearchToggle = 'chartSearchToggle',
}

export type WebVitalsMonitoringRequestPayload = {
    pathname: string;
    serverTimings: { [key: string]: number } | null;
    metrics: NextWebVitalsMetric[];
};
